import CheckIcon from '@mui/icons-material/Check';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { INewApartmentForm } from '../../containers/DeviceModal/components/NewApartmentForm/validationSchema';
import apartmentActions, { selectors as apartmentSelectors } from '../../modules/apartments';
import { IModalBase } from '../../modules/commonTypes';
import deviceActions, { selectors as deviceSelectors } from '../../modules/devices';
import { IDevice } from '../../modules/devices/store/deviceStore';
import ActionModal from '../_DesignSystemWrappers/Modal/ActionModal';
import messages from './messages';
import { SetApartmentFormField } from './SetApartmentFormField';

export interface ISetApartmentModalProps extends IModalBase {
  device?: IDevice;
}

function SetApartmentModal({ device, onClose, open }: ISetApartmentModalProps) {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const isEditing = useSelector(deviceSelectors.isDeviceEditing);
  const isOptionsLoading = useSelector(apartmentSelectors.getApartmentsListIsFetching);
  const pendingClose = useRef(false);

  const formMethods = useForm<INewApartmentForm>({
    defaultValues: {
      apartmentId: device?.apartmentId,
      apartmentName: '',
      floor: '',
      hasForm: false,
      hasSelectedApartment: false,
      isApartmentRequired: false,
      number: '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (device?.site?.company?.id && device?.site?.id) {
      dispatch(apartmentActions.getApartmentsList(device.site.company.id, device.site.id));
    }
  }, [dispatch, device?.site?.company?.id, device?.site?.id]);

  useEffect(() => {
    formMethods.reset({
      apartmentId: device?.apartmentId,
      apartmentName: '',
      floor: '',
      hasForm: false,
      hasSelectedApartment: false,
      isApartmentRequired: false,
      number: '',
    });
  }, [device?.apartmentId]);

  useEffect(() => {
    if (pendingClose.current && !isEditing) {
      pendingClose.current = false;
      onClose();
    }
  }, [isEditing, onClose]);

  function handleConfirm() {
    const { apartmentId } = formMethods.getValues();
    pendingClose.current = true;
    dispatch(
      deviceActions.editDevice({
        apartmentId: apartmentId,
        externalId: device?.externalId,
        id: device?.id,
        name: device?.name,
        userId: device?.userId,
      })
    );
  }

  return (
    <FormProvider {...formMethods}>
      <ActionModal
        icon={<CheckIcon />}
        isPrimaryButtonDisabled={isEditing}
        isPrimaryButtonLoading={isEditing}
        muiDialogProps={{
          maxWidth: 'xs',
        }}
        onClose={onClose}
        open={open}
        primaryButtonAction={handleConfirm}
        primaryButtonText={formatMessage(messages.deviceApartmentModalConfirm)}
        secondaryButtonText={formatMessage(messages.deviceApartmentModalCancel)}
        title={formatMessage(messages.deviceApartmentModalHeader)}
      >
        <Typography sx={{ mb: 3 }}>{formatMessage(messages.deviceApartmentModalDescription)}</Typography>
        {isOptionsLoading ? (
          <Box display="flex" justifyContent="center" sx={{ overflow: 'hidden', py: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          <SetApartmentFormField deviceType={device?.type} disabled={isEditing} />
        )}
      </ActionModal>
    </FormProvider>
  );
}

export default SetApartmentModal;
